import { useEffect, useMemo, useRef, useState } from "react";

import EapTreeRow from "@components/dashboard/eap/EapTreeRow";
import { useProjectActions, useProjectContext } from "@contexts/projects";

const LEVELS = Object.freeze(["unit", "cell", "step", "substep", "service"]);
const CHILD_LEVEL = Object.freeze({
  unit: "cell",
  cell: "step",
  step: "substep",
  substep: "service",
});
const COLLECTION = Object.freeze({
  cell: "cells",
  step: "steps",
  substep: "substeps",
  service: "services",
});
const LABELS = Object.freeze({
  unit: "Unidade",
  cell: "Célula",
  step: "Etapa",
  substep: "Subetapa",
  service: "Serviço",
});
const OFFSETS = Object.freeze({
  unit: 3,
  cell: 5,
  step: 8,
  substep: 11,
  service: 14,
});

export const normalizeExtraValueLevel = (value) => {
  const singular = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/s$/, "");
  return LEVELS.includes(singular) ? singular : null;
};

export const levelForCode = (code, root) =>
  Object.entries(OFFSETS).find(
    ([, offset]) => String(code).length - String(root).length === offset,
  )?.[0] ?? null;

export const isExtraValueTargetEligible = (level, appropriationLevel) =>
  normalizeExtraValueLevel(level) ===
    normalizeExtraValueLevel(appropriationLevel) &&
  normalizeExtraValueLevel(level) !== null;

const descriptionOf = (item) =>
  item?.description ??
  item?.wbs_description ??
  item?.building_unit_description ??
  item?.building_unit_name ??
  item?.name ??
  item?.wbs ??
  item?.wbs_code_id;

const SearchIcon = () => (
  <svg
    className="extra-values-eap__search-icon"
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 20 20"
    width="16"
    height="16"
  >
    <circle
      cx="8.5"
      cy="8.5"
      r="5.75"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
    />
    <path
      d="m13 13 4 4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
  </svg>
);

const ExtraValuesEap = ({
  indicators = new Map(),
  readOnly = false,
  onCreate,
}) => {
  const { activeProject } = useProjectContext();
  const {
    loadUnits,
    loadCells,
    loadSteps,
    loadSubsteps,
    loadServices,
    getExtraValueTargets,
  } = useProjectActions();
  const workId = activeProject?._id;
  const roots =
    activeProject?.wbs_code_ids ??
    (activeProject?.wbs_code_id ? [activeProject.wbs_code_id] : []);
  const appropriationLevel = normalizeExtraValueLevel(
    activeProject?.appropriation_level,
  );
  const [expanded, setExpanded] = useState(() => new Set());
  const [nodeState, setNodeState] = useState({});
  const [search, setSearch] = useState("");
  const [selectedWbs, setSelectedWbs] = useState(null);
  const [result, setResult] = useState({
    status: "idle",
    items: [],
    page: 0,
    total: 0,
    hasMore: false,
  });
  const generation = useRef(0);

  const loaderFor = {
    unit: loadCells,
    cell: loadSteps,
    step: loadSubsteps,
    substep: loadServices,
  };

  useEffect(() => {
    setExpanded(new Set());
    setNodeState({});
    setSearch("");
    setSelectedWbs(null);
    setResult({ status: "idle", items: [], page: 0, total: 0, hasMore: false });
    if (workId) loadUnits();
  }, [workId]);

  const loadChildren = async (level, code) => {
    const loader = loaderFor[level];
    if (!loader) return [];
    setNodeState((current) => ({ ...current, [code]: "loading" }));
    const rows = await loader(code);
    let status = "error";
    if (Array.isArray(rows)) status = rows.length ? "ready" : "empty";
    setNodeState((current) => ({
      ...current,
      [code]: status,
    }));
    return rows;
  };

  const toggle = async (level, code) => {
    if (!CHILD_LEVEL[level]) return;
    if (expanded.has(code)) {
      setExpanded((current) => {
        const next = new Set(current);
        next.delete(code);
        return next;
      });
      return;
    }
    setExpanded((current) => new Set(current).add(code));
    const collection = COLLECTION[CHILD_LEVEL[level]];
    if (!activeProject?.[collection]?.[code]) await loadChildren(level, code);
  };

  const runSearch = async ({ page = 0, append = false, signal } = {}) => {
    generation.current += 1;
    const { current } = generation;
    setResult((previous) => ({
      ...previous,
      status: append ? "loading_more" : "loading",
    }));
    const response = await getExtraValueTargets({
      search: search.trim(),
      page,
      pageSize: 50,
      signal,
    });
    if (current !== generation.current) return;
    if (!response?.ok || !Array.isArray(response.items)) {
      setResult((previous) => ({
        ...previous,
        status:
          response?.errorCode === "request_aborted" ? previous.status : "error",
      }));
      return;
    }
    setResult((previous) => ({
      status: "ready",
      items: append ? [...previous.items, ...response.items] : response.items,
      page,
      total: response.total,
      hasMore: response.hasMore,
    }));
  };

  useEffect(() => {
    const query = search.trim();
    if (query.length < 2) {
      generation.current += 1;
      setResult({
        status: "idle",
        items: [],
        page: 0,
        total: 0,
        hasMore: false,
      });
      return undefined;
    }
    // Invalida imediatamente a consulta anterior durante o debounce; uma resposta antiga
    // não ganha uma janela de 300 ms para publicar resultados da busca substituída.
    generation.current += 1;
    const controller = new AbortController();
    const timer = setTimeout(
      () => runSearch({ signal: controller.signal }),
      300,
    );
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [workId, search]);

  const reveal = async (target) => {
    setSelectedWbs(target.wbsCodeId);
    await loadUnits();
    const ancestors = (target.path ?? [])
      .map((entry) => entry.wbsCodeId)
      .filter((code) => code && code !== target.root);
    await ancestors.reduce(async (previous, code) => {
      await previous;
      const level = levelForCode(code, target.root);
      if (level && CHILD_LEVEL[level]) return loadChildren(level, code);
      return undefined;
    }, Promise.resolve());
    setExpanded((current) => new Set([...current, ...ancestors]));
    setTimeout(() => {
      const row = document.getElementById(
        `extra-values-eap-${target.wbsCodeId}`,
      );
      row?.scrollIntoView?.({ behavior: "auto", block: "center" });
      row?.focus?.();
    }, 0);
  };

  const unitGroups = useMemo(() => {
    const groups = new Map(roots.map((root) => [root, []]));
    (activeProject?.units ?? []).forEach((unit) => {
      const root =
        unit.wbs_code_id_building ??
        roots.find((candidate) =>
          String(unit.wbs_code_id ?? "").startsWith(candidate),
        ) ??
        roots[0];
      if (!groups.has(root)) groups.set(root, []);
      groups.get(root).push(unit);
    });
    return [...groups];
  }, [activeProject?.units, roots.join("|")]);

  const renderRows = (items, level, depth, root) =>
    (items ?? []).map((item) => {
      const code = String(item.wbs_code_id ?? "");
      const childLevel = CHILD_LEVEL[level];
      const children = childLevel
        ? activeProject?.[COLLECTION[childLevel]]?.[code]
        : null;
      const isExpanded = expanded.has(code);
      const eligible = isExtraValueTargetEligible(level, appropriationLevel);
      const indicator = indicators.get(code);
      const childrenId = `extra-values-eap-children-${code}`;
      let state = null;
      if (indicator) {
        state = `${indicator.count} ${
          indicator.count === 1 ? "lançamento" : "lançamentos"
        } · ${indicator.label}`;
      } else if (eligible) {
        state = "Disponível para novo lançamento";
      }
      return (
        <EapTreeRow
          key={code}
          variant="singleColumn"
          level={level}
          label={descriptionOf(item)}
          eyebrow={
            <span className="accordion-label">
              {`EAP ${item.wbs ?? code} / ${LABELS[level]}`}
            </span>
          }
          state={state}
          expandable={Boolean(childLevel)}
          expanded={isExpanded}
          onToggle={() => toggle(level, code)}
          rowId={`extra-values-eap-${code}`}
          childrenId={childrenId}
          tabIndex={-1}
          ariaCurrent={selectedWbs === code ? "true" : undefined}
          identityClassName="extra-values-eap__identity"
          actionsClassName="extra-values-eap__actions"
          rowClassName={eligible ? "is-eligible" : ""}
          actions={
            eligible && !readOnly ? (
              <button
                type="button"
                className="button button-accent small"
                onClick={() => onCreate?.({ ...item, level, root })}
              >
                Adicionar estouro ou economia
              </button>
            ) : null
          }
          after={
            isExpanded ? (
              <div
                id={childrenId}
                className="extra-values-eap__children"
                role="group"
                aria-label={`Filhos de ${descriptionOf(item)}`}
              >
                {nodeState[code] === "loading" ? (
                  <p role="status">Carregando…</p>
                ) : null}
                {nodeState[code] === "error" ? (
                  <p role="alert">
                    Falha ao carregar.{" "}
                    <button
                      type="button"
                      onClick={() => loadChildren(level, code)}
                    >
                      Tentar novamente
                    </button>
                  </p>
                ) : null}
                {nodeState[code] === "empty" ? (
                  <p role="status">Este item não possui filhos.</p>
                ) : null}
                {Array.isArray(children)
                  ? renderRows(children, childLevel, depth + 1, root)
                  : null}
              </div>
            ) : null
          }
        />
      );
    });

  return (
    <section
      className="extra-values-eap"
      aria-labelledby="extra-values-eap-title"
    >
      <header className="extra-values-eap__header">
        <div>
          <h2 id="extra-values-eap-title">EAP da obra</h2>
          <p>Navegue pela estrutura e crie lançamentos no nível permitido.</p>
        </div>
        <span className="extra-values-eap__appropriation" role="status">
          {appropriationLevel
            ? `Apropriação em ${LABELS[appropriationLevel].toLocaleLowerCase(
                "pt-BR",
              )}`
            : "Nível de apropriação não reconhecido"}
        </span>
      </header>
      {!appropriationLevel ? (
        <p className="alert alert-error" role="alert">
          Corrija o cadastro da obra para criar lançamentos. A EAP continua
          disponível para consulta.
        </p>
      ) : null}
      <div className="extra-values-eap__search" role="search">
        <label htmlFor="extra-values-eap-search">Buscar na EAP</label>
        <div className="extra-values-eap__search-control">
          <SearchIcon />
          <input
            id="extra-values-eap-search"
            type="search"
            aria-label="Buscar na EAP por código ou descrição"
            aria-describedby="extra-values-eap-search-hint"
            placeholder="Código ou descrição — ex.: 22.001 ou elevador"
            autoComplete="off"
            spellCheck={false}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setSearch("");
            }}
          />
          {search ? (
            <button
              type="button"
              className="extra-values-eap__search-clear"
              aria-label="Limpar o texto da busca"
              onClick={() => setSearch("")}
            >
              ×
            </button>
          ) : null}
        </div>
        <span
          id="extra-values-eap-search-hint"
          className="extra-values-eap__search-hint"
        >
          Alcança todos os níveis, mesmo os que ainda não estão abertos. Ignora
          acento.
        </span>
      </div>
      {result.status !== "idle" ? (
        <div className="extra-values-eap__search-results" aria-live="polite">
          {result.status === "loading" ? <p>Buscando…</p> : null}
          {result.status === "error" ? (
            <p role="alert">
              Não foi possível buscar.{" "}
              <button type="button" onClick={() => runSearch()}>
                Tentar novamente
              </button>
            </p>
          ) : null}
          {result.status === "ready" && result.items.length === 0 ? (
            <p>Nenhum alvo encontrado.</p>
          ) : null}
          {result.items.map((item) => (
            <button
              key={`${item.root}:${item.wbsCodeId}`}
              type="button"
              onClick={() => reveal(item)}
            >
              <strong>{item.code}</strong> {item.description}
              <small>
                {[
                  ...item.path.map((entry) => entry.description),
                  item.description,
                ].join(" › ")}
              </small>
            </button>
          ))}
          {result.hasMore ? (
            <button
              type="button"
              disabled={result.status === "loading_more"}
              onClick={() => runSearch({ page: result.page + 1, append: true })}
            >
              {result.status === "loading_more"
                ? "Carregando…"
                : "Carregar mais"}
            </button>
          ) : null}
        </div>
      ) : null}
      <div className="extra-values-eap__tree">
        {!activeProject?.units ? <p role="status">Carregando EAP…</p> : null}
        {Array.isArray(activeProject?.units) &&
        activeProject.units.length === 0 ? (
          <p role="status">A EAP está vazia.</p>
        ) : null}
        <div className="accordion value-accordion-tree">
          {unitGroups.map(([root, units]) => (
            <div key={root} className="extra-values-eap__root">
              {unitGroups.length > 1 ? <h3>{root}</h3> : null}
              {renderRows(units, "unit", 0, root)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExtraValuesEap;
